'use client';

import type { RefObject } from 'react';
import { useEffect } from 'react';

/**
 * A hook that listens for clicks outside of a specific ref, and calls a callback when it happens.
 *
 * @params onClickAway - Callback fired when a "click away" event is detected. This needs to be dependency-stable, so don't forget to use `useCallback`.
 * @params strategy - The strategy to use for listening to click events. 'start' listens for mousedown and touchstart events, 'click' listens for full click events.
 */
export const useClickAway = (
  ref: RefObject<HTMLElement | null>,
  {
    onClickAway,
    strategy = 'start',
  }: { onClickAway: (event: MouseEvent | TouchEvent) => void; strategy?: 'start' | 'click' },
) => {
  useEffect(() => {
    const handleClickAway = (event: MouseEvent | TouchEvent) => {
      if (
        ref.current &&
        ref.current instanceof HTMLElement &&
        !ref.current.contains(event.target as Node)
      ) {
        onClickAway(event);
      }
    };

    if (strategy === 'start') {
      document.addEventListener('mousedown', handleClickAway);
      document.addEventListener('touchstart', handleClickAway);
    } else {
      document.addEventListener('click', handleClickAway);
    }

    return () => {
      if (strategy === 'start') {
        document.removeEventListener('mousedown', handleClickAway);
        document.removeEventListener('touchstart', handleClickAway);
      } else {
        document.removeEventListener('click', handleClickAway);
      }
    };
  }, [onClickAway, ref, strategy]);
};
